import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  JsonInput,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import type { ItemModelInput, QuestionDifficulty, QuestionType } from '@prepzio/shared';
import { useState } from 'react';

import { InfoTip } from '../components/InfoTip';
import { SkillPicker } from '../components/skill-picker';
import {
  useCreateItemModel,
  useGenerateFromItemModel,
  useItemModels,
  usePreviewItemModel,
} from '../hooks/use-content';
import { useEnumLabel, usePlural, useT } from '../i18n';
import { errorMessage } from '../lib/ui';

const DIFFICULTIES: QuestionDifficulty[] = ['easy', 'medium', 'hard'];
const TYPES: QuestionType[] = ['free_text', 'mcq', 'coding'];

/** The example from PRE-293, so a blank form is never the first thing anybody sees. */
const EXAMPLE_SLOTS = JSON.stringify(
  {
    API: ['fetch', 'fs.readFile', 'axios.get'],
    SYMPTOM: ['undefined', 'Promise { <pending> }', 'the values in the wrong order'],
  },
  null,
  2,
);
const EXAMPLE_CONSTRAINTS = JSON.stringify(
  [
    {
      if: { slot: 'API', value: 'fs.readFile' },
      then: { slot: 'SYMPTOM', oneOf: ['undefined', 'the values in the wrong order'] },
    },
  ],
  null,
  2,
);

/**
 * Item models (PRE-293) — write the shape of a question, not the question.
 *
 * The screen is built around one asymmetry: everything except the last button is
 * free. Preview expands the model with no model call at all, so the loop an
 * expert actually works in — change a slot, see what comes out, change a
 * constraint, look again — costs nothing and can be run as often as it takes.
 * Generating is the deliberate last step.
 *
 * What the expert sees is therefore the MODEL and its expansion, never the
 * finished questions: the finished text is the part the machine writes, and a
 * screen that led with it would invite editing the output instead of the
 * specification that produced it.
 */
export function ItemModelsScreen() {
  const t = useT();
  const plural = usePlural();
  const label = useEnumLabel();
  const models = useItemModels();
  const preview = usePreviewItemModel();
  const create = useCreateItemModel();
  const generate = useGenerateFromItemModel();

  const [title, setTitle] = useState('');
  const [skillId, setSkillId] = useState('');
  const [difficulty, setDifficulty] = useState<QuestionDifficulty>('easy');
  const [type, setType] = useState<QuestionType>('free_text');
  const [stem, setStem] = useState('');
  const [slotsText, setSlotsText] = useState(EXAMPLE_SLOTS);
  const [constraintsText, setConstraintsText] = useState(EXAMPLE_CONSTRAINTS);
  const [concepts, setConcepts] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [jsonError, setJsonError] = useState<string | null>(null);

  /** The form as the API wants it, or null when the JSON boxes do not parse. */
  function build(status: 'draft' | 'active'): ItemModelInput | null {
    try {
      const parsed = {
        title,
        skillId,
        difficulty,
        type,
        stem,
        slots: JSON.parse(slotsText) as Record<string, string[]>,
        constraints: JSON.parse(constraintsText) as ItemModelInput['constraints'],
        expectedConcepts: concepts
          .split('\n')
          .map((c) => c.trim())
          .filter(Boolean),
        status,
      };
      setJsonError(null);
      return parsed;
    } catch (err) {
      setJsonError(t('itemModels.badJson', { message: (err as Error).message }));
      return null;
    }
  }

  const previewData = preview.data ?? null;
  const canSubmit = title.trim().length >= 3 && skillId !== '' && stem.trim().length >= 20;

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Group gap={6} mb={4}>
          <Text fw={600}>{t('itemModels.writeTitle')}</Text>
          <InfoTip label={t('itemModels.writeTip')} />
        </Group>
        <Text c="dimmed" size="sm" mb="md">
          {t('itemModels.writeHint')}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="sm">
          <TextInput
            label={t('itemModels.title')}
            placeholder={t('itemModels.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          <SkillPicker
            label={t('common.skill')}
            value={skillId || null}
            onChange={(v) => setSkillId(v ?? '')}
          />
          <Select
            label={t('content.difficulty')}
            data={DIFFICULTIES.map((d) => ({ value: d, label: label('difficulty', d) }))}
            value={difficulty}
            onChange={(v) => setDifficulty((v as QuestionDifficulty) ?? 'easy')}
            allowDeselect={false}
          />
          <Select
            label={t('content.type')}
            data={TYPES.map((v) => ({ value: v, label: label('type', v) }))}
            value={type}
            onChange={(v) => setType((v as QuestionType) ?? 'free_text')}
            allowDeselect={false}
          />
        </SimpleGrid>

        <Textarea
          label={t('itemModels.stem')}
          description={t('itemModels.stemHint')}
          placeholder={t('itemModels.stemPlaceholder')}
          autosize
          minRows={3}
          value={stem}
          onChange={(e) => setStem(e.currentTarget.value)}
          mb="sm"
        />

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" mb="sm">
          <JsonInput
            label={t('itemModels.slots')}
            description={t('itemModels.slotsHint')}
            autosize
            minRows={6}
            value={slotsText}
            onChange={setSlotsText}
          />
          <JsonInput
            label={t('itemModels.constraints')}
            description={t('itemModels.constraintsHint')}
            autosize
            minRows={6}
            value={constraintsText}
            onChange={setConstraintsText}
          />
        </SimpleGrid>

        <Textarea
          label={t('itemModels.concepts')}
          description={t('itemModels.conceptsHint')}
          autosize
          minRows={2}
          value={concepts}
          onChange={(e) => setConcepts(e.currentTarget.value)}
          mb="md"
        />

        <Group gap="sm">
          <Button
            variant="default"
            loading={preview.isPending}
            disabled={!canSubmit}
            onClick={() => {
              const input = build('draft');
              if (input) preview.mutate(input);
            }}
          >
            {t('itemModels.previewFree')}
          </Button>
          <Button
            loading={create.isPending}
            disabled={!canSubmit || (previewData?.problems.length ?? 1) > 0}
            onClick={() => {
              const input = build('active');
              if (input) create.mutate(input);
            }}
          >
            {t('itemModels.saveActive')}
          </Button>
        </Group>

        {jsonError && (
          <Text c="red" size="sm" mt="sm">
            {jsonError}
          </Text>
        )}
        {preview.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(preview.error)}
          </Text>
        )}
        {create.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(create.error)}
          </Text>
        )}

        {previewData && previewData.problems.length > 0 && (
          <Alert color="red" variant="light" mt="md" title={t('itemModels.fixFirst')}>
            <Stack gap={2}>
              {previewData.problems.map((p) => (
                <Text key={p} size="sm">
                  {p}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {previewData && previewData.problems.length === 0 && (
          <Alert color="blue" variant="light" mt="md">
            <Text fw={600} size="sm" mb={6}>
              {plural('itemModels.yield_one', 'itemModels.yield', previewData.maxYield)}
            </Text>
            <Stack gap={6}>
              {previewData.items.map((item) => (
                <Text key={item.stem} size="sm">
                  {item.stem}
                </Text>
              ))}
            </Stack>
            {previewData.maxYield > previewData.items.length && (
              <Text c="dimmed" size="sm" mt={6}>
                {t('itemModels.andMore', {
                  count: previewData.maxYield - previewData.items.length,
                })}
              </Text>
            )}
          </Alert>
        )}
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Group gap={6} mb="sm">
          <Text fw={600}>{t('itemModels.listTitle')}</Text>
          <InfoTip label={t('itemModels.listTip')} />
        </Group>

        {models.isLoading && (
          <Text c="dimmed" size="sm">
            {t('common.loading')}
          </Text>
        )}
        {models.data?.items.length === 0 && (
          <Text c="dimmed" size="sm">
            {t('itemModels.none')}
          </Text>
        )}

        <Stack gap="sm">
          {(models.data?.items ?? []).map((m) => (
            <Card key={m.id} withBorder padding="md" radius="sm">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <div style={{ minWidth: 0 }}>
                  <Group gap="xs" mb={4}>
                    <Text fw={600}>{m.title}</Text>
                    <Badge variant="light" color={m.status === 'active' ? 'green' : 'gray'}>
                      {t(
                        m.status === 'active'
                          ? 'itemModels.status.active'
                          : 'itemModels.status.draft',
                      )}
                    </Badge>
                    <Badge variant="light" color="gray">
                      {m.skillTitle ?? m.skillId} · {label('difficulty', m.difficulty)} ·{' '}
                      {label('type', m.type)}
                    </Badge>
                  </Group>
                  <Code block>{m.stem}</Code>
                  <Text c="dimmed" size="sm" mt={6}>
                    {t('itemModels.summary', {
                      yield: m.maxYield,
                      produced: m.produced,
                      slots: Object.keys(m.slots).join(', ') || t('itemModels.noSlots'),
                      constraints:
                        m.constraints.length > 0
                          ? t('itemModels.constraintCount', { count: m.constraints.length })
                          : '',
                    })}
                  </Text>
                </div>
                <Group gap="xs" wrap="nowrap" align="flex-end">
                  <NumberInput
                    label={t('itemModels.items')}
                    w={90}
                    min={1}
                    max={Math.max(1, m.maxYield)}
                    value={counts[m.id] ?? Math.min(5, Math.max(1, m.maxYield))}
                    onChange={(v) =>
                      setCounts((c) => ({ ...c, [m.id]: Math.max(1, Number(v) || 1) }))
                    }
                  />
                  <Button
                    size="sm"
                    disabled={m.status !== 'active' || m.maxYield === 0}
                    loading={generate.isPending && generate.variables?.id === m.id}
                    onClick={() =>
                      generate.mutate({
                        id: m.id,
                        count: counts[m.id] ?? Math.min(5, Math.max(1, m.maxYield)),
                      })
                    }
                  >
                    {t('itemModels.generate')}
                  </Button>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>

        {generate.isError && (
          <Text c="red" size="sm" mt="sm">
            {errorMessage(generate.error)}
          </Text>
        )}
        {generate.data && (
          <Alert color="blue" variant="light" mt="md">
            {plural('itemModels.written_one', 'itemModels.written', generate.data.count, {
              attempted: generate.data.attempted,
              extra: [
                generate.data.failed > 0
                  ? t('itemModels.failedSuffix', { count: generate.data.failed })
                  : '',
                generate.data.duplicatesFlagged > 0
                  ? t('itemModels.dupSuffix', { count: generate.data.duplicatesFlagged })
                  : '',
                generate.data.malformedRejected > 0
                  ? t('itemModels.malformedSuffix', { count: generate.data.malformedRejected })
                  : '',
              ].join(''),
            })}
          </Alert>
        )}
      </Card>
    </Stack>
  );
}
